import React, { Dispatch, SetStateAction, useContext, useEffect, useRef } from 'react';
import { Components } from './styled';
import SelectOptionModal from '../../../../components/modals/SelectOptionModal';
import { ModalOptionType } from '../../../../components/modals/SelectOptionModal/types';
import useModalState from '../../../../hooks/useModalState';
import CreateOrEditLessonModal from '../../../../components/modals/CreateOrEditLessonModal';
import ProjectTableModal from '../../../../components/ProjectTableModal';
import { modalConfigs } from '../../../../components/ProjectTableModal/constants';
import { Lesson } from '../../../../api/types/lesson';
import { updateLesson } from '../../../../api/lesson';
import { ProjectTableDataType } from '../../../../components/ProjectTable/types';
import { isProjectError } from '../../../../utils/errors';
import { ActivityIndicatorContext } from '../../../../providers/ActivityIndicatorProvider/context';
import { ToastContext } from '../../../../providers/ToastProvider/context';
import { parseDateToStringWithFormat } from '../../../../utils/dateFormat';
import { format } from '../../../../utils/dateFormat/types';
import { LARGE_PROJECT_MODAL_WIDTH } from '../../../../utils/styled/dimensions';
import { AppSettingsContext } from '../../../../providers/AppSettingsProvider/context';
import { UpdateLessonDto } from '../../../../api/typeDtos/lesson';
import ProjectPicker from '../../../../components/ProjectPicker';
import ProjectSearchDropdown from '../../../../components/ProjectSearchDropdown';
import useCustomizedDropdown from '../../../../hooks/useCustomizedDropdown';
import SelectNetworkModal from '../../../../components/modals/SelectNetworkModal';

const ModalOptionValues = {
  createLesson: 'createLesson',
  selectLesson: 'selectLesson',
};

const CreateOrLinkLesson = ({
  didStartFlow,
  setDidStartFlow,
  refreshCallback,
}: {
  didStartFlow: Date | undefined;
  setDidStartFlow: Dispatch<SetStateAction<Date | undefined>>;
  refreshCallback?: () => Promise<void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);
  const { categories, pillarTypes, pillarExpertises } = useContext(AppSettingsContext);

  const localDate = useRef<Date | undefined>();

  const selectOptionProps = useModalState<boolean>();
  const createLessonProps = useModalState<{ lesson?: Lesson; publishedAtDate: Date }>();
  const selectLessonProps = useModalState<boolean>();
  const selectNetworkProps = useModalState<string>();

  const lessonToPublish = useRef<Lesson>();

  const { props: pillarTypeProps } = useCustomizedDropdown({
    possibleValues: pillarTypes,
    singleSelect: true,
    customType: 'pillarType',
  });
  const { props: pillarExpertiseProps } = useCustomizedDropdown({
    possibleValues: pillarExpertises,
    singleSelect: true,
    customType: 'pillarExpertise',
  });
  const { props: categoryProps } = useCustomizedDropdown({
    possibleValues: categories,
    singleSelect: true,
  });

  useEffect(() => {
    if (didStartFlow) {
      localDate.current = didStartFlow;
      selectOptionProps.open(true);
      setDidStartFlow(undefined);
    }
  }, [didStartFlow]);

  const onSelectOption = (state: string | undefined) => {
    if (state) {
      if (state === ModalOptionValues.createLesson && localDate.current) {
        createLessonProps.open({ publishedAtDate: localDate.current });
      } else if (state === ModalOptionValues.selectLesson) {
        selectLessonProps.open(true);
      }
    }
    selectOptionProps.onModalClose();
  };

  const onSelectLessons = async (data: ProjectTableDataType | string[]) => {
    const lesson = data?.[0] as Lesson | undefined;
    if (localDate.current && lesson) {
      lessonToPublish.current = lesson;
      selectLessonProps.onModalClose();
      selectNetworkProps.open(lesson?.title);
    } else {
      selectLessonProps.onModalClose();
    }
  };

  const onSelectNetwork = async (
    postToNetworkId: string | undefined,
    postTitle: string | undefined,
    postContent: string | undefined,
  ) => {
    const lesson = lessonToPublish.current;
    selectNetworkProps.onModalClose();
    if (localDate.current && lesson) {
      const publishedAt = localDate.current?.getTime();
      setIsLoading(true);
      const dataUpdates: UpdateLessonDto = {
        id: lesson?.id,
        publishedAt: publishedAt,
        isDailyLesson: true,
        ...(postToNetworkId && { postToNetworkId }),
        ...(postTitle && { postTitle }),
        ...(postContent && { postContent }),
      };
      const result = await updateLesson(dataUpdates);
      setIsLoading(false);
      const resultError = isProjectError(result);
      if (resultError) {
        showErrorToast(resultError);
      } else {
        const displayDate = parseDateToStringWithFormat(localDate.current, format.weekDayMonthDayYear);
        showSuccessToast(`Lesson is now published for ${displayDate}`);
        refreshCallback?.().then();
      }
    }
    lessonToPublish.current = undefined;
  };

  // Dismissing the modal must not silently abandon the publish - the network step is
  // optional, but reaching it means the admin already committed to publishing the lesson.
  const onSelectNetworkModalClose = () => {
    onSelectNetwork(undefined, undefined, undefined).then();
  };

  const onCreateOrEditModalClose = (refreshTrigger?: boolean) => {
    if (refreshTrigger) {
      refreshCallback?.().then();
    }
    createLessonProps.onModalClose();
  };

  const options: ModalOptionType[] = [
    { label: 'Create a new Daily Lesson', value: ModalOptionValues.createLesson },
    { label: 'Select an existing lesson', value: ModalOptionValues.selectLesson },
  ];
  return (
    <>
      <SelectOptionModal
        {...selectOptionProps}
        modalTitle={'Create or select lesson'}
        options={options}
        onContinue={onSelectOption}
      />
      <CreateOrEditLessonModal
        {...createLessonProps}
        onModalClose={onCreateOrEditModalClose}
        refreshCallback={refreshCallback}
      />
      <ProjectTableModal
        modalConfig={modalConfigs.selectLessons({
          category: categoryProps.selectedItems?.[0]?.value,
          pillarType: pillarTypeProps.selectedItems?.[0]?.value,
          pillarExpertise: pillarExpertiseProps.selectedItems?.[0]?.value,
        })}
        {...selectLessonProps}
        onModalSubmit={onSelectLessons}
        singleSelectionOnly
        modalTitle={'Select a lesson'}
        modalWidth={LARGE_PROJECT_MODAL_WIDTH}
        sortingModel={{ field: 'publishedAt', sort: 'desc' }}
        tableSearchChildren={
          <Components.SearchBarChildrenWrapper>
            <Components.SearchBarSeparator />
            <Components.FiltersWrapper>
              <ProjectPicker dropdownProps={pillarTypeProps} notSetText={'Pillar type'} />
              <ProjectSearchDropdown {...pillarTypeProps} />
              <ProjectPicker dropdownProps={pillarExpertiseProps} notSetText={'Pillar expertise'} />
              <ProjectSearchDropdown {...pillarExpertiseProps} />
              <ProjectPicker dropdownProps={categoryProps} notSetText={'Category'} />
              <ProjectSearchDropdown {...categoryProps} />
            </Components.FiltersWrapper>
          </Components.SearchBarChildrenWrapper>
        }
      />
      <SelectNetworkModal
        {...selectNetworkProps}
        onModalClose={onSelectNetworkModalClose}
        onContinue={onSelectNetwork}
        lessonTitle={selectNetworkProps.modalState}
      />
    </>
  );
};

export default CreateOrLinkLesson;
